import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Toggle } from '../../../../../CommonComponents/OPL/Toggle/Toggle';
import { OrionManagementStyleModal } from '../OrionManagementStyleModal/OrionManagementStyleModal';
import { useToggleClassificationTypeMutation } from '../../api/useToggleClassificationTypeMutation';
import { useToggleAdvisorClassificationTypeMutation } from '../../api/useToggleAdviserClassificationTypeMutation';
import { selectHasAdvisorAssetBucketingLayoutAccess } from '../../../../../CommonSelectors/CommonAreaSelector';
import { updateAssetBucketingHeadline } from '../../../../../AdviserArea/AdviserAreaActions';
import { FormFieldValues } from '../../AssetBucketing.meta';
import { ModalType } from './ManagamentStyleToggle.meta';

interface Props {
  isDefaultAssetBucketingLayout: boolean;
}

export const ManagementStyleToggle = ({ isDefaultAssetBucketingLayout }: Props) => {
  const { watch } = useFormContext();

  const canEnableManagementStylesBucketing = watch('canEnableManagementStylesBucketing');

  if (!canEnableManagementStylesBucketing) {
    return null;
  }

  return (
    <ManagementStyleToggleContent isDefaultAssetBucketingLayout={isDefaultAssetBucketingLayout} />
  );
};

const ManagementStyleToggleContent = ({ isDefaultAssetBucketingLayout }: Props) => {
  const [isModalVisible, setIsModalVisible] = useState<ModalType | null>(null);

  const dispatch = useDispatch();

  const {
    formState: { isDirty },
    reset,
    getValues,
    watch,
    setValue,
  } = useFormContext();

  const enableManagementStylesBucketingSelection = watch('enableManagementStylesBucketing');

  const hasAdvisorAssetBucketingLayoutAccess = useSelector(
    selectHasAdvisorAssetBucketingLayoutAccess,
  );

  const toggleClassificationMutation = useToggleClassificationTypeMutation();
  const toggleAdvisorClassificationMutation = useToggleAdvisorClassificationTypeMutation();

  const { mutateAsync: saveToggle, isLoading, isError } = hasAdvisorAssetBucketingLayoutAccess
    ? toggleAdvisorClassificationMutation
    : toggleClassificationMutation;

  const onSave = async () => {
    try {
      const formValues = getValues() as FormFieldValues;
      const data = {
        ...formValues,

        isDefaultAssetBucketingLayout: isDefaultAssetBucketingLayout
          ? !isDirty
          : isDefaultAssetBucketingLayout,
      };

      const { enableManagementStylesBucketing, ...assetBucketsData } = await saveToggle(data);

      // In order to trigger a navigate away warning.
      // Keep toggle value the same.
      // And then setValue
      reset({
        ...assetBucketsData,
        enableManagementStylesBucketing: data.enableManagementStylesBucketing,
      });

      setValue('enableManagementStylesBucketing', enableManagementStylesBucketing, {
        shouldDirty: true,
      });

      dispatch(updateAssetBucketingHeadline(assetBucketsData.headline));

      setIsModalVisible(null);

      toast.success(
        assetBucketsData.enableManagementStylesBucketing
          ? 'Settings have been updated to Orion management styles.'
          : 'Settings have been reverted to their default values.',
      );
    } catch (err) {
      if (err instanceof Error) {
        toast.error(err.message);
      }
    }
  };

  return (
    <>
      <Toggle
        checked={enableManagementStylesBucketingSelection}
        onChange={event => {
          if (event.target.checked) {
            return setIsModalVisible(ModalType.UseOrionManagementStyleModal);
          }

          return setIsModalVisible(ModalType.UseAssets);
        }}
      >
        Use Orion Management Style
      </Toggle>

      <OrionManagementStyleModal
        isOpen={isModalVisible}
        onClose={() => setIsModalVisible(null)}
        isLoading={isLoading}
        isError={isError}
        onSave={onSave}
      />
    </>
  );
};
